import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";

const API_BASE = "https://api.getfitindia.in/api";

function CourtOwnerBlockSlots() {
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState("");
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]); // ✅ New
  const [selectedSlots, setSelectedSlots] = useState([]); // ✅ New
  const [blockType, setBlockType] = useState("single");
  const [date, setDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  // 🔐 Auth header
  const getAuthHeader = () => {
    const token = localStorage.getItem("courtOwnerToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // 🏟 Load venues
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/courtowner/venues`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error("Failed to load venues");
        const data = await res.json();
        setVenues(data);
      } catch (e) {
        console.error(e);
        alert("Failed to load venues");
      }
    })();
  }, []);

  // 🎾 Load courts for selected venue
  useEffect(() => {
    if (!selectedVenue) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error("Failed to load courts");
        const data = await res.json();
        setCourts(Array.isArray(data) ? data : data.courts || []);
      } catch (e) {
        console.error(e);
        alert("Failed to load courts");
      }
    })();
  }, [selectedVenue]);

  // ⏱ Load available slots for that court (from ManageSlots)
  useEffect(() => {
    if (!selectedVenue || !selectedCourt) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${selectedVenue}/courts`, {
          headers: { ...getAuthHeader() },
        });
        const data = await res.json();
        const court = Array.isArray(data)
          ? data.find((c) => c._id === selectedCourt)
          : data.courts?.find((c) => c._id === selectedCourt);
        setAvailableSlots(court?.slots || []);
      } catch (err) {
        console.error(err);
        alert("Failed to load available slots");
      }
    })();
  }, [selectedCourt, selectedVenue]);

  // 🚫 Load blocked slots for selected court
  useEffect(() => {
    if (!selectedCourt) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/courtowner/blockslots/${selectedCourt}${
            selectedDate ? `?date=${selectedDate}` : ""
          }`,
          { headers: { ...getAuthHeader() } }
        );
        if (!res.ok) throw new Error("Failed to fetch blocked slots");
        const data = await res.json();
        setBlockedSlots(data || []);
      } catch (e) {
        console.error(e);
        alert("Failed to load blocked slots");
      }
    })();
  }, [selectedCourt, selectedDate]);

  // ✅ Toggle slot selection
  const toggleSlot = (slot) => {
    setSelectedSlots((prev) => {
      const exists = prev.find(
        (s) => s.startTime === slot.startTime && s.endTime === slot.endTime
      );
      return exists
        ? prev.filter(
            (s) => s.startTime !== slot.startTime || s.endTime !== slot.endTime
          )
        : [...prev, slot];
    });
  };

  // 🧩 Handle Block Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVenue || !selectedCourt)
      return alert("Please select a venue and court first");
    if (selectedSlots.length === 0)
      return alert("Select at least one slot to block");

    const body =
      blockType === "single"
        ? {
            venueId: selectedVenue,
            courtId: selectedCourt,
            date,
            slots: selectedSlots,
          }
        : {
            venueId: selectedVenue,
            courtId: selectedCourt,
            startDate,
            endDate,
            slots: selectedSlots,
          };

    try {
      const res = await fetch(`${API_BASE}/courtowner/blockslots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || "Failed to block slots");
      }

      alert("✅ Slot(s) blocked successfully");
      setSelectedSlots([]);
      setDate("");
      setStartDate("");
      setEndDate("");

      // Refresh blocked slots
      const refreshed = await fetch(
        `${API_BASE}/courtowner/blockslots/${selectedCourt}`,
        { headers: { ...getAuthHeader() } }
      );
      const updated = await refreshed.json();
      setBlockedSlots(updated);
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // 🗑 Unblock Slot
  const handleUnblock = async (slotId) => {
    if (!window.confirm("Unblock this slot?")) return;
    try {
      const res = await fetch(`${API_BASE}/courtowner/blockslots/${slotId}`, {
        method: "DELETE",
        headers: { ...getAuthHeader() },
      });
      if (!res.ok) throw new Error("Failed to unblock slot");
      alert("Slot unblocked successfully");
      setBlockedSlots((prev) => prev.filter((b) => b._id !== slotId));
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  // 🗓 Format date
  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="flex">
      <CourtOwnerSidebar />
      <div className="flex-1 p-6 bg-gray-50 min-h-screen">
        <div className="p-6 max-w-5xl mx-auto bg-white rounded-xl shadow-lg">

          {/* --- Navigation Tabs --- */}
          <div className="flex flex-wrap gap-3 mb-6">
            <NavLink
              to="/courtowner/manageslots"
              className={({ isActive }) =>
                `px-5 py-2 rounded font-medium transition ${
                  isActive
                    ? "bg-[#b37400] text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`
              }
            >
              Manage Courts & Slots
            </NavLink>

            <NavLink
              to="/courtowner/blockslots"
              className={({ isActive }) =>
                `px-5 py-2 rounded font-medium transition ${
                  isActive
                    ? "bg-[#009688] text-white"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`
              }
            >
              Block Slots
            </NavLink>
          </div>

          <h2 className="text-2xl font-bold mb-6 text-green-700">🕒 Block Slots</h2>

          {/* --- Block Form --- */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block font-medium">Select Venue</label>
                <select
                  value={selectedVenue}
                  onChange={(e) => setSelectedVenue(e.target.value)}
                  className="w-full p-2 border rounded mt-1"
                  required
                >
                  <option value="">-- Choose Venue --</option>
                  {venues.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium">Select Court</label>
                <select
                  value={selectedCourt}
                  onChange={(e) => setSelectedCourt(e.target.value)}
                  className="w-full p-2 border rounded mt-1"
                  required
                >
                  <option value="">-- Choose Court --</option>
                  {courts.map((c, idx) => (
                    <option key={c._id || idx} value={c._id || ""}>
                      {c.courtName || c.name || `Court ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* --- Available Slots --- */}
            {availableSlots.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium mb-2">Select Slots to Block</h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {availableSlots.map((slot, idx) => (
                    <label
                      key={idx}
                      className={`border rounded p-2 cursor-pointer ${
                        selectedSlots.some(
                          (s) =>
                            s.startTime === slot.startTime &&
                            s.endTime === slot.endTime
                        )
                          ? "bg-red-100 border-red-400"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSlots.some(
                          (s) =>
                            s.startTime === slot.startTime &&
                            s.endTime === slot.endTime
                        )}
                        onChange={() => toggleSlot(slot)}
                        className="mr-2"
                      />
                      {slot.startTime} - {slot.endTime}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* --- Date Section --- */}
            {blockType === "single" ? (
              <div>
                <label className="block font-medium mt-4">Select Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="p-2 border rounded mt-1"
                  required
                />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium mt-4">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded mt-1"
                    required
                  />
                </div>
                <div>
                  <label className="block font-medium mt-4">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded mt-1"
                    required
                  />
                </div>
              </div>
            )}

            {/* --- Block Button --- */}
            <button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded mt-4"
            >
              Block Selected Slots
            </button>
          </form>

          {/* --- Blocked Slots List --- */}
          <div className="mt-10">
            <h3 className="text-xl font-semibold mb-4">🚫 Currently Blocked</h3>
            {blockedSlots.length === 0 ? (
              <p className="text-gray-500">No blocked slots found.</p>
            ) : (
              <ul className="space-y-3">
                {blockedSlots.map((b) => (
                  <li
                    key={b._id}
                    className="flex justify-between items-center border p-3 rounded"
                  >
                    <div>
                      {b.date ? (
                        <span>
                          {formatDate(b.date)} • {b.startTime} - {b.endTime}
                        </span>
                      ) : (
                        <span>
                          {formatDate(b.startDate)} → {formatDate(b.endDate)} •{" "}
                          {b.startTime} - {b.endTime}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleUnblock(b._id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Unblock
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>                                      
      </div>
    </div>
  );
}

export default CourtOwnerBlockSlots;
